import React from "react";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import type { UnitReconciliation } from "../hooks/useUnitReconciliation";
import MeasureValueCell from "./MeasureValueCell";

interface UnitReconciliationPanelProps {
  month: string;
  reconciliation: UnitReconciliation | null;
}

/**
 * The two per-unit figures side by side (FR-002c, SC-009): CP-388 deliverable 1.
 *
 * The financial model counts "active CP" (connectors) while operations think in sites, and the same month
 * therefore yields two materially different per-unit numbers: CP-388 measured roughly €174 per site
 * against €153 per connector. Neither is wrong, and the argument that follows from quoting one without the
 * other is entirely avoidable, the ratio between them is exactly the average number of active connectors
 * per active site, and that is checkable rather than assertable.
 *
 * So the panel shows both, shows the factor, and states whether the identity actually holds. If it ever
 * does not, one of the two views is wrong and the badge says so rather than leaving it to be discovered in
 * a meeting.
 */
const UnitReconciliationPanel: React.FC<UnitReconciliationPanelProps> = ({ month, reconciliation }) => {
  if (!reconciliation) return null;

  const {
    perSite,
    perConnector,
    activeSites,
    activeConnectors,
    connectorsPerSite,
    observedRatio,
    reconciles,
  } = reconciliation;

  return (
    <Card className="mb-3">
      <div className="flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <div className="font-semibold mb-1">
            Money per unit, {month} — both bases
            <span className="text-color-secondary font-normal ml-2 text-sm">
              CP-388 deliverable 1
            </span>
          </div>
          <div className="text-color-secondary text-sm" style={{ maxWidth: "44rem" }}>
            The financial model counts active connectors; operations count sites. Same month, same money —
            two different denominators, so two different per-unit figures. Quoting one without the other is
            where the disagreement comes from.
          </div>
        </div>
        {reconciles ? (
          <Tag
            value="bases reconcile"
            severity="success"
            title="The ratio between the two figures equals the average active connectors per active site, as it must"
          />
        ) : (
          <Tag
            value="bases do NOT reconcile"
            severity="danger"
            title="The two figures do not differ by the connectors-per-site factor. One of the two views is wrong — do not quote either until this is understood."
          />
        )}
      </div>

      <div className="grid mt-3">
        <div className="col-12 md:col-4">
          <div className="text-color-secondary text-sm">Per site</div>
          <div className="text-2xl font-semibold">
            <MeasureValueCell measure={perSite.moneyCents} format="money" />
          </div>
          <div className="text-color-secondary text-xs">{activeSites} active site(s)</div>
        </div>
        <div className="col-12 md:col-4">
          <div className="text-color-secondary text-sm">Per connector — the model's &ldquo;active CP&rdquo;</div>
          <div className="text-2xl font-semibold">
            <MeasureValueCell measure={perConnector.moneyCents} format="money" />
          </div>
          <div className="text-color-secondary text-xs">{activeConnectors} active connector(s)</div>
        </div>
        <div className="col-12 md:col-4">
          <div className="text-color-secondary text-sm">Factor between them</div>
          <div className="text-2xl font-semibold">
            {connectorsPerSite === null ? "—" : `×${connectorsPerSite.toFixed(3)}`}
          </div>
          <div className="text-color-secondary text-xs">
            connectors per site
            {observedRatio !== null && (
              <>
                {" "}· observed ×{observedRatio.toFixed(3)}
              </>
            )}
          </div>
        </div>
      </div>

      {!reconciles && (
        <Message
          className="mt-3 w-full"
          severity="error"
          text="The per-site figure divided by the per-connector figure should equal the average active connectors per active site. It does not. Treat both figures as unreliable until this is explained — it means the two reporting units are not reading the same underlying totals."
        />
      )}
    </Card>
  );
};

export default UnitReconciliationPanel;
